import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { BarChart3, Copy, Edit3, Eye, Plus, Save, Trash2, X, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUESTION_TYPES = [
  { value: "short_text", label: "Short text" },
  { value: "long_text", label: "Long text" },
  { value: "email", label: "Email" },
  { value: "single_choice", label: "Single choice" },
  { value: "multi_choice", label: "Multiple choice" },
  { value: "rating", label: "Rating (1-5)" },
];

const NEEDS_OPTIONS = ["single_choice", "multi_choice"];

const blankSurvey = () => ({
  title: "",
  description: "",
  survey_type: "native",
  external_url: "",
  embed_html: "",
  questions: [],
  is_active: true,
});

const blankQuestion = () => ({ label: "", q_type: "short_text", options: [], required: false });

export function AdminSurveys({ authHeaders }) {
  const [surveys, setSurveys] = useState([]);
  const [editing, setEditing] = useState(null); // survey object being edited/created
  const [responsesFor, setResponsesFor] = useState(null); // survey object
  const [responses, setResponses] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/surveys`, { headers: authHeaders });
      setSurveys(res.data || []);
    } catch {
      toast.error("Could not load surveys");
    }
  }, [authHeaders]);

  useEffect(() => { load(); }, [load]);

  const startCreate = () => setEditing(blankSurvey());
  const startEdit = (s) => setEditing({ ...s, questions: (s.questions || []).map((q) => ({ ...q, options: q.options || [] })) });

  const setField = (field, value) => setEditing((cur) => ({ ...cur, [field]: value }));

  const addQuestion = () => setEditing((cur) => ({ ...cur, questions: [...cur.questions, blankQuestion()] }));
  const updateQuestion = (idx, field, value) =>
    setEditing((cur) => ({ ...cur, questions: cur.questions.map((q, i) => (i === idx ? { ...q, [field]: value } : q)) }));
  const removeQuestion = (idx) => setEditing((cur) => ({ ...cur, questions: cur.questions.filter((_, i) => i !== idx) }));

  const save = async () => {
    if (!editing.title.trim()) { toast.error("Survey needs a title"); return; }
    const payload = {
      ...editing,
      questions: editing.survey_type === "native"
        ? editing.questions.map((q) => ({
            ...q,
            options: NEEDS_OPTIONS.includes(q.q_type)
              ? (Array.isArray(q.options) ? q.options : String(q.options || "").split("\n")).map((o) => o.trim()).filter(Boolean)
              : [],
          }))
        : [],
    };
    setBusy(true);
    try {
      if (editing.id) {
        await axios.put(`${API}/admin/surveys/${editing.id}`, payload, { headers: authHeaders });
        toast.success("Survey updated");
      } else {
        await axios.post(`${API}/admin/surveys`, payload, { headers: authHeaders });
        toast.success("Survey created");
      }
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save survey");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.title}" and all its responses?`)) return;
    try {
      await axios.delete(`${API}/admin/surveys/${s.id}`, { headers: authHeaders });
      toast.success("Survey deleted");
      await load();
    } catch {
      toast.error("Could not delete survey");
    }
  };

  const viewResponses = async (s) => {
    setResponsesFor(s);
    setResponses([]);
    try {
      const res = await axios.get(`${API}/admin/surveys/${s.id}/responses`, { headers: authHeaders });
      setResponses(res.data || []);
    } catch {
      toast.error("Could not load responses");
    }
  };

  const download = async (s, format) => {
    try {
      const res = await axios.get(`${API}/admin/surveys/${s.id}/responses/export`, { params: { format }, headers: authHeaders, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${s.title.replace(/[^A-Za-z0-9_-]+/g, "_")}_responses.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} downloaded`);
    } catch (err) {
      console.warn("Export failed", err);
      toast.error("Could not export responses");
    }
  };

  const copyLink = (s) => {
    const url = `${window.location.origin}/surveys/${s.id}`;
    try { navigator.clipboard.writeText(url); toast.success("Survey link copied"); }
    catch { toast.message(url); }
  };

  const optionsToText = (q) => (Array.isArray(q.options) ? q.options.join("\n") : q.options || "");

  return (
    <section className="admin-panel wide" data-testid="admin-surveys-panel">
      <div className="enquiry-header">
        <h2>Surveys</h2>
        <Button className="primary-cta save-app" onClick={startCreate} data-testid="admin-survey-new-button"><Plus size={16} /> New survey</Button>
      </div>

      <div className="admin-survey-list" data-testid="admin-survey-list">
        {surveys.map((s) => (
          <article key={s.id} className="admin-survey-row" data-testid={`admin-survey-row-${s.id}`}>
            <div className="admin-survey-info">
              <strong>{s.title}</strong>
              <span className={`survey-type-badge type-${s.survey_type}`}>{s.survey_type}</span>
              <span className={`survey-active-chip ${s.is_active ? "on" : "off"}`} data-testid={`admin-survey-active-${s.id}`}>{s.is_active ? "Active" : "Hidden"}</span>
              <em data-testid={`admin-survey-count-${s.id}`}>{s.response_count || 0} responses</em>
            </div>
            <div className="admin-survey-actions">
              {s.survey_type !== "link" && (
                <button className="status-btn success" onClick={() => viewResponses(s)} data-testid={`admin-survey-responses-${s.id}`}><BarChart3 size={13} /> Responses</button>
              )}
              <button className="status-btn muted" onClick={() => copyLink(s)} data-testid={`admin-survey-copy-${s.id}`}><Copy size={13} /> Link</button>
              <button className="status-btn good" onClick={() => startEdit(s)} data-testid={`admin-survey-edit-${s.id}`}><Edit3 size={13} /> Edit</button>
              <button className="status-btn delete" onClick={() => remove(s)} data-testid={`admin-survey-delete-${s.id}`}><Trash2 size={13} /> Delete</button>
            </div>
          </article>
        ))}
        {!surveys.length && <p data-testid="admin-no-surveys-message">No surveys yet. Create your first one.</p>}
      </div>

      {editing && (
        <div className="survey-modal-overlay" onClick={() => setEditing(null)} data-testid="admin-survey-editor-overlay">
          <div className="survey-modal admin-survey-editor" onClick={(e) => e.stopPropagation()} data-testid="admin-survey-editor">
            <div className="survey-modal-bar">
              <strong>{editing.id ? "Edit survey" : "New survey"}</strong>
              <button onClick={() => setEditing(null)} data-testid="admin-survey-editor-close"><X size={18} /></button>
            </div>
            <div className="admin-survey-editor-body">
              <label>Title</label>
              <Input value={editing.title} onChange={(e) => setField("title", e.target.value)} data-testid="admin-survey-title-input" />
              <label>Description</label>
              <Textarea value={editing.description} onChange={(e) => setField("description", e.target.value)} data-testid="admin-survey-description-input" />

              <label>Type</label>
              <select value={editing.survey_type} onChange={(e) => setField("survey_type", e.target.value)} data-testid="admin-survey-type-select">
                <option value="native">Native form (build questions here)</option>
                <option value="link">External link</option>
                <option value="html">HTML / embed (live preview)</option>
              </select>

              <label className="admin-survey-active-toggle">
                <input type="checkbox" checked={editing.is_active} onChange={(e) => setField("is_active", e.target.checked)} data-testid="admin-survey-active-toggle" />
                <span>Active (visible on public surveys page)</span>
              </label>

              {editing.survey_type === "link" && (
                <>
                  <label>External survey URL</label>
                  <Input value={editing.external_url} onChange={(e) => setField("external_url", e.target.value)} placeholder="https://forms.gle/..." data-testid="admin-survey-external-url-input" />
                </>
              )}

              {editing.survey_type === "html" && (
                <>
                  <label>Embed HTML / iframe</label>
                  <Textarea value={editing.embed_html} onChange={(e) => setField("embed_html", e.target.value)} rows={6} placeholder='<iframe src="..."></iframe> or full HTML' data-testid="admin-survey-embed-html-input" />
                </>
              )}

              {editing.survey_type === "native" && (
                <div className="admin-question-builder" data-testid="admin-question-builder">
                  <div className="enquiry-header"><h2 style={{ fontSize: 15 }}>Questions</h2><Button className="ghost-cta-dark" onClick={addQuestion} data-testid="admin-add-question-button"><Plus size={14} /> Add question</Button></div>
                  {editing.questions.map((q, i) => (
                    <div key={q.id || i} className="admin-question-row" data-testid={`admin-question-row-${i}`}>
                      <Input value={q.label} onChange={(e) => updateQuestion(i, "label", e.target.value)} placeholder="Question text" data-testid={`admin-question-label-${i}`} />
                      <div className="admin-question-controls">
                        <select value={q.q_type} onChange={(e) => updateQuestion(i, "q_type", e.target.value)} data-testid={`admin-question-type-${i}`}>
                          {QUESTION_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                        <label className="admin-req-toggle"><input type="checkbox" checked={q.required} onChange={(e) => updateQuestion(i, "required", e.target.checked)} data-testid={`admin-question-required-${i}`} /> Required</label>
                        <button className="status-btn delete" onClick={() => removeQuestion(i)} data-testid={`admin-question-remove-${i}`}><Trash2 size={13} /></button>
                      </div>
                      {NEEDS_OPTIONS.includes(q.q_type) && (
                        <Textarea
                          value={optionsToText(q)}
                          onChange={(e) => updateQuestion(i, "options", e.target.value.split("\n"))}
                          rows={3}
                          placeholder="One option per line"
                          data-testid={`admin-question-options-${i}`}
                        />
                      )}
                    </div>
                  ))}
                  {!editing.questions.length && <p className="admin-hint">Add at least one question for a native survey.</p>}
                </div>
              )}

              <Button className="primary-cta full" onClick={save} disabled={busy} data-testid="admin-survey-save-button"><Save size={16} /> {busy ? "Saving…" : "Save survey"}</Button>
            </div>
          </div>
        </div>
      )}

      {responsesFor && (
        <div className="survey-modal-overlay" onClick={() => setResponsesFor(null)} data-testid="admin-responses-overlay">
          <div className="survey-modal admin-survey-editor" onClick={(e) => e.stopPropagation()} data-testid="admin-responses-modal">
            <div className="survey-modal-bar">
              <strong><Eye size={15} /> {responsesFor.title} · {responses.length} responses</strong>
              <div className="admin-export-btns">
                <button className="status-btn good" onClick={() => download(responsesFor, "csv")} disabled={!responses.length} data-testid="admin-responses-download-csv"><Download size={13} /> CSV</button>
                <button className="status-btn muted" onClick={() => download(responsesFor, "json")} disabled={!responses.length} data-testid="admin-responses-download-json"><Download size={13} /> JSON</button>
              </div>
              <button onClick={() => setResponsesFor(null)} data-testid="admin-responses-close"><X size={18} /></button>
            </div>
            <div className="admin-survey-editor-body">
              {responsesFor.survey_type === "html" && <p className="admin-hint" data-testid="admin-responses-html-hint">Answers are captured automatically when someone submits your pasted HTML form. Field names become the column headers.</p>}
              {!responses.length && <p className="admin-hint" data-testid="admin-no-responses">No responses yet.</p>}
              {responses.map((r, ri) => (
                <article key={r.id} className="admin-response-card" data-testid={`admin-response-${ri}`}>
                  <span className="admin-response-date">{new Date(r.created_at).toLocaleString()}</span>
                  {r.answers.map((a, ai) => (
                    <div key={`${a.question_id || a.label}-${ai}`} className="admin-response-answer">
                      <strong>{a.label}</strong>
                      <span>{a.value || "—"}</span>
                    </div>
                  ))}
                </article>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
